import type { VisitType } from './types';

const EDIT_LOCK_WINDOW_MS = 24 * 60 * 60 * 1000;
const SUBMISSION_LOCK_PREFIX = 'PaSubmissionLockV2';

export interface IntegrationContext {
  aid?: string | null;
  eid?: string | null;
  referralMessageId?: string | null;
}

export function isIntegratedVisit(context: IntegrationContext): boolean {
  return Boolean(context.aid || context.eid || context.referralMessageId);
}

export function buildSubmissionLockStorageKey({
  shortId,
  aid,
  eid,
  referralMessageId,
}: {
  shortId?: string | null;
} & IntegrationContext): string {
  return [SUBMISSION_LOCK_PREFIX, shortId || '', referralMessageId || '', aid || '', eid || ''].join(':');
}

export function setSubmissionLockedAt(storageKey: string, submittedAtIso: string): void {
  try {
    localStorage.setItem(storageKey, submittedAtIso);
  } catch (error) {
    console.warn('Failed to persist submission lock timestamp:', error);
  }
}

function parseDateMs(value?: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getVisitLockAnchorMs(visit?: VisitType): number | null {
  return parseDateMs(visit?.updatedAt) ?? parseDateMs(visit?.visitedAt) ?? parseDateMs(visit?.createdAt);
}

export function isSubmissionEditLocked({
  visit,
  localSubmittedAt,
  isIntegrated,
  now = Date.now(),
}: {
  visit?: VisitType;
  localSubmittedAt?: string | null;
  isIntegrated: boolean;
  now?: number;
}): boolean {
  if (isIntegrated) return false;

  const isReported = visit?.status === 'reported';
  const localLockAnchorMs = parseDateMs(localSubmittedAt);
  const lockAnchorMs = isReported
    ? (localLockAnchorMs ?? getVisitLockAnchorMs(visit))
    : localLockAnchorMs;

  if (!isReported && lockAnchorMs == null) return false;
  if (lockAnchorMs == null) return isReported;

  const elapsedMs = now - lockAnchorMs;
  if (elapsedMs < 0) return true;
  return elapsedMs < EDIT_LOCK_WINDOW_MS;
}
